import { DateTime } from 'luxon';
import { InputSelectOption } from '@types';
import { MULTI_DAY_WORK_FORM_FIELDS, MultiDayDayBlock } from '../multi-day-work.types';

const capitalizeWords = (value: string): string =>
  value.replace(/(^|[\s,])([a-zà-ü])/gi, (_, prefix: string, letter: string) => {
    return `${prefix}${letter.toUpperCase()}`;
  });

export const getMultiDayDayBlockFieldPath = <FieldName extends keyof MultiDayDayBlock>(
  index: number,
  fieldName: FieldName
): `${typeof MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks}.${number}.${FieldName}` =>
  `${MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks}.${index}.${fieldName}`;

export const formatMultiDayBlockDate = (date: string): string =>
  DateTime.fromISO(date, { locale: 'nl' }).toFormat('ccc d LLL yyyy');

export const formatMultiDayBlockHeaderDate = (date: string): string =>
  capitalizeWords(DateTime.fromISO(date, { locale: 'nl' }).toFormat('d LLL, ccc'));

export const formatMultiDayBlockPreviewTime = (time: string): string =>
  time.trim().length > 0 ? time : '--:--';

export const getMultiDayDayBlockBreakLabel = (
  breakMinutes: string | undefined,
  breakOptions: InputSelectOption[]
): string => {
  if (breakMinutes == null || breakMinutes === '') {
    return '–';
  }

  const matchingOption = breakOptions.find(
    (option) => String(option.value) === String(breakMinutes)
  );

  const label = matchingOption?.label;
  return typeof label === 'string' ? label : '–';
};

export const createDayBlockFromTemplate = (
  date: string,
  templateBlock: Pick<MultiDayDayBlock, 'startTime' | 'endTime' | 'breakMinutes'>
): MultiDayDayBlock => ({
  date,
  startTime: templateBlock.startTime,
  endTime: templateBlock.endTime,
  breakMinutes: templateBlock.breakMinutes,
});

export const sortDayBlocksByDate = (blocks: MultiDayDayBlock[]): MultiDayDayBlock[] =>
  [...blocks].sort(
    (firstBlock, secondBlock) =>
      DateTime.fromISO(firstBlock.date).toMillis() - DateTime.fromISO(secondBlock.date).toMillis()
  );

export const getDayOneTemplateBlock = (
  blocks: MultiDayDayBlock[]
): Pick<MultiDayDayBlock, 'startTime' | 'endTime' | 'breakMinutes'> => {
  const dayOneBlock = sortDayBlocksByDate(blocks)[0];

  if (dayOneBlock) {
    return {
      startTime: dayOneBlock.startTime,
      endTime: dayOneBlock.endTime,
      breakMinutes: dayOneBlock.breakMinutes,
    };
  }

  return {
    startTime: '',
    endTime: '',
    breakMinutes: undefined,
  };
};

export const syncBlocksWithDates = (
  dates: string[],
  existingBlocks: MultiDayDayBlock[],
  templateBlock: Pick<MultiDayDayBlock, 'startTime' | 'endTime' | 'breakMinutes'>
): MultiDayDayBlock[] => {
  const existingBlocksByDate = new Map(existingBlocks.map((block) => [block.date, block] as const));

  const syncedBlocks = dates.map((date) => {
    const existingBlock = existingBlocksByDate.get(date);

    if (existingBlock) {
      return existingBlock;
    }

    return createDayBlockFromTemplate(date, templateBlock);
  });

  return sortDayBlocksByDate(syncedBlocks);
};
